import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/utils/api";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Search, Wallet, DollarSign } from "lucide-react";

const AgentDashboard = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [foundUser, setFoundUser] = useState(null);
  const [topupForm, setTopupForm] = useState({
    amount: 2000,
    plan_type: "Mini Pack",
    plan_duration_days: 15,
    daily_topup_limit: 10,
  });
  const [loading, setLoading] = useState(false);

  const plans = [
    { name: "Mini Pack", price: 2000, duration: 15, limit: 10, bonus: 0 },
    { name: "Starter Pack", price: 5000, duration: 30, limit: 20, bonus: 250 },
    { name: "Business Pack", price: 10000, duration: 30, limit: 50, bonus: 1000 },
    { name: "Professional Pack", price: 20000, duration: 60, limit: 100, bonus: 4000 },
    { name: "Enterprise Pack", price: 40000, duration: 90, limit: 200, bonus: 16000 },
  ];

  const handleSearch = async () => {
    if (!searchTerm) {
      toast.error("Please enter email or mobile number");
      return;
    }

    setLoading(true);
    try {
      const response = await api.get(`/agent/search-user?search=${searchTerm}`);
      setFoundUser(response.data);
      toast.success("User found!");
    } catch (error) {
      setFoundUser(null);
      toast.error(error.response?.data?.detail || "User not found");
    } finally {
      setLoading(false);
    }
  };

  const handleTopup = async () => {
    if (!foundUser) {
      toast.error("Please search and select a user first");
      return;
    }

    setLoading(true);
    try {
      await api.post("/agent/topup", {
        user_email_or_mobile: foundUser.email,
        ...topupForm,
      });
      toast.success("Top-up successful!");
      setFoundUser(null);
      setSearchTerm("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to top-up");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2" style={{fontFamily: 'Inter, sans-serif'}}>
            Agent Dashboard
          </h1>
          <p className="text-gray-400">Manage wallet top-ups for users</p>
          <div className="mt-4 p-3 bg-blue-600/20 border border-blue-500/50 rounded-lg">
            <p className="text-white text-sm font-semibold">⚠️ Agent Permissions:</p>
            <p className="text-gray-300 text-sm mt-1">You can ONLY search users and top-up their wallets. No other actions are allowed.</p>
          </div>
        </div>

        {/* Search User */}
        <Card className="p-6 bg-white/5 backdrop-blur-sm border-white/10 mb-8">
          <h2 className="text-2xl font-bold text-white mb-4">Search User</h2>
          <div className="flex gap-4">
            <Input
              data-testid="agent-search-input"
              type="text"
              placeholder="Enter email or mobile number"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            />
            <Button
              data-testid="agent-search-button"
              onClick={handleSearch}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>
        </Card>

        {/* Found User */}
        {foundUser && (
          <Card className="p-6 bg-white/5 backdrop-blur-sm border-white/10 mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">User Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-gray-400 text-sm">Name</p>
                <p className="text-white font-semibold">{foundUser.name}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Email</p>
                <p className="text-white font-semibold">{foundUser.email}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Mobile</p>
                <p className="text-white font-semibold">{foundUser.mobile}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm">Current Balance</p>
                <p className="text-white font-semibold">₹{foundUser.wallet_balance}</p>
              </div>
            </div>
          </Card>
        )}

        {/* Top-up Form */}
        {foundUser && (
          <Card className="p-6 bg-white/5 backdrop-blur-sm border-white/10">
            <h2 className="text-2xl font-bold text-white mb-6">Top-up Wallet</h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Select Plan</label>
                <Select
                  value={topupForm.plan_type}
                  onValueChange={(value) => {
                    const plan = plans.find(p => p.name === value);
                    if (plan) {
                      setTopupForm({
                        amount: plan.price,
                        plan_type: plan.name,
                        plan_duration_days: plan.duration,
                        daily_topup_limit: plan.limit,
                      });
                    }
                  }}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map((plan) => (
                      <SelectItem key={plan.name} value={plan.name}>
                        {plan.name} - ₹{plan.price} ({plan.duration} days, {plan.limit} posts/day)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-400 text-sm">Amount</p>
                    <p className="text-white text-xl font-bold">₹{topupForm.amount}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Duration</p>
                    <p className="text-white text-xl font-bold">{topupForm.plan_duration_days} days</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Daily Limit</p>
                    <p className="text-white text-xl font-bold">{topupForm.daily_topup_limit} posts</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">New Balance</p>
                    <p className="text-green-400 text-xl font-bold">
                      ₹{foundUser.wallet_balance + topupForm.amount}
                    </p>
                  </div>
                </div>
              </div>

              <Button
                data-testid="agent-topup-button"
                onClick={handleTopup}
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                {loading ? "Processing..." : "Process Top-up"}
              </Button>
            </div>
          </Card>
        )}

        {/* Instructions */}
        {!foundUser && (
          <Card className="p-8 bg-white/5 backdrop-blur-sm border-white/10">
            <h3 className="text-xl font-bold text-white mb-4">Instructions</h3>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-start">
                <span className="text-blue-400 mr-2">•</span>
                Search for users by their registered email or mobile number
              </li>
              <li className="flex items-start">
                <span className="text-blue-400 mr-2">•</span>
                Select a plan to add credits to their wallet
              </li>
              <li className="flex items-start">
                <span className="text-blue-400 mr-2">•</span>
                The user will receive immediate access to post/bump ads
              </li>
              <li className="flex items-start">
                <span className="text-blue-400 mr-2">•</span>
                All transactions are logged with your agent ID
              </li>
            </ul>
          </Card>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default AgentDashboard;
